/**
 * CreditHistoryModal - Modal d'affichage de l'historique des transactions de crédits
 * 
 * Affiche toutes les transactions (déductions, ajouts, top-ups, etc.) avec :
 * - Filtres par type de transaction
 * - Pagination
 * - Détails de chaque transaction
 * 
 * Utilisation :
 * ```tsx
 * <CreditHistoryModal
 *   id="creditHistoryModal"
 *   companyId={companyId}
 *   accessToken={accessToken}
 *   onClose={() => setShowHistory(false)}
 * />
 * ```
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import CreditsService from '../../services/api/creditsService';
import { Spiner } from '../../components';

interface CreditTransaction {
    id: string;
    transaction_type: 'DEDUCT' | 'ADD' | 'TOPUP' | 'GIFT' | 'REFUND' | 'ROLLOVER' | 'ADJUST';
    action_type?: 'GENERATION' | 'ANALYSIS' | 'MAPPING_UNLOCK';
    credits_amount: number;
    credits_before: number;
    credits_after: number;
    created_at: string;
    metadata?: {
        process_id?: string;
        process_name?: string;
        analysis_id?: string;
        map_id?: string;
        estimated_credits?: number;
        actual_credits?: number;
        tokens?: number;
        [key: string]: any;
    };
    description?: string;
}

interface CreditHistoryModalProps {
    id: string;
    companyId: string;
    accessToken: string;
    onClose?: () => void;
}

const CreditHistoryModal: React.FC<CreditHistoryModalProps> = ({
    id,
    companyId,
    accessToken,
    onClose
}) => {
    const { t } = useTranslation();
    const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<string>('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const itemsPerPage = 20;
    
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const loadTransactions = useCallback(async (page: number = 1, transactionFilter: string = 'ALL') => {
        setLoading(true);
        setError(null);
        
        try {
            const skip = (page - 1) * itemsPerPage;
            const response = await CreditsService.getCreditHistory(
                companyId,
                accessToken,
                itemsPerPage,
                skip
            );

            if (response.success && response.data) {
                // La réponse peut être directement un array ou un objet avec transactions
                let transactionsList = Array.isArray(response.data) 
                    ? response.data 
                    : (response.data.transactions || response.data.data || []);
                
                // Filtrer par type si nécessaire
                if (transactionFilter !== 'ALL') {
                    transactionsList = transactionsList.filter((t: CreditTransaction) => 
                        t.transaction_type === transactionFilter
                    );
                }

                setTransactions(transactionsList);
                const total = Array.isArray(response.data) 
                    ? response.data.length 
                    : (response.data.total || transactionsList.length);
                setTotalTransactions(total);
                setTotalPages(Math.ceil(total / itemsPerPage));
            } else {
                setError(response.message || 'Erreur lors du chargement de l\'historique');
            }
        } catch (err) {
            console.error('[CreditHistoryModal] Load error:', err);
            setError('Erreur réseau lors du chargement de l\'historique');
        } finally {
            setLoading(false);
        }
    }, [companyId, accessToken]);

    useEffect(() => {
        loadTransactions(currentPage, filter);
    }, [loadTransactions, currentPage, filter]);

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const getTransactionIcon = (type: string) => {
        switch (type) {
            case 'DEDUCT':
                return '➖';
            case 'ADD':
            case 'TOPUP':
            case 'GIFT':
                return '➕';
            case 'REFUND':
                return '↩️';
            case 'ROLLOVER':
                return '🔄';
            case 'ADJUST':
                return '⚙️';
            default:
                return '📝';
        }
    };

    const getTransactionColor = (type: string) => {
        switch (type) {
            case 'DEDUCT':
                return 'text-danger';
            case 'ADD':
            case 'TOPUP':
            case 'GIFT':
                return 'text-success';
            case 'REFUND':
                return 'text-warning';
            case 'ROLLOVER':
                return 'text-info';
            case 'ADJUST':
                return 'text-secondary';
            default:
                return 'text-white';
        }
    };

    const getTransactionLabel = (transaction: CreditTransaction) => {
        if (transaction.description) {
            return transaction.description;
        }

        const actionLabels: Record<string, string> = {
            'GENERATION': t('views.modal.creditHistory.action.generation', 'Génération de processus'),
            'ANALYSIS': t('views.modal.creditHistory.action.analysis', 'Analyse de processus'),
            'MAPPING_UNLOCK': t('views.modal.creditHistory.action.mappingUnlock', 'Déverrouillage de cartographie')
        };

        const typeLabels: Record<string, string> = {
            'DEDUCT': t('views.modal.creditHistory.type.deduct', 'Déduction'),
            'ADD': t('views.modal.creditHistory.type.add', 'Ajout'),
            'TOPUP': t('views.modal.creditHistory.type.topup', 'Recharge'),
            'GIFT': t('views.modal.creditHistory.type.gift', 'Cadeau'),
            'REFUND': t('views.modal.creditHistory.type.refund', 'Remboursement'),
            'ROLLOVER': t('views.modal.creditHistory.type.rollover', 'Report de crédits'),
            'ADJUST': t('views.modal.creditHistory.type.adjust', 'Ajustement')
        };

        if (transaction.action_type && actionLabels[transaction.action_type]) {
            return `${typeLabels[transaction.transaction_type] || transaction.transaction_type} - ${actionLabels[transaction.action_type]}`;
        }

        return typeLabels[transaction.transaction_type] || transaction.transaction_type;
    };

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ zIndex: 10001, pointerEvents: 'auto', maxWidth: '900px' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', backgroundColor: '#1a1a1a' }}>
                    <div className="modal-header border-secondary">
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            📊 {t('views.modal.creditHistory.title', 'Historique des crédits')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body text-white">
                        {/* Filtres */}
                        <div className="mb-3">
                            <label className="form-label text-theme">
                                {t('views.modal.creditHistory.filter', 'Filtrer par type')}:
                            </label>
                            <select
                                className="form-select bg-dark text-white border-secondary"
                                value={filter}
                                onChange={(e) => {
                                    setFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="ALL">{t('views.modal.creditHistory.filterAll', 'Tous')}</option>
                                <option value="DEDUCT">{t('views.modal.creditHistory.filterDeduct', 'Déductions')}</option>
                                <option value="ADD">{t('views.modal.creditHistory.filterAdd', 'Ajouts')}</option>
                                <option value="TOPUP">{t('views.modal.creditHistory.filterTopup', 'Recharges')}</option>
                                <option value="GIFT">{t('views.modal.creditHistory.filterGift', 'Cadeaux')}</option>
                                <option value="REFUND">{t('views.modal.creditHistory.filterRefund', 'Remboursements')}</option>
                                <option value="ROLLOVER">{t('views.modal.creditHistory.filterRollover', 'Reports')}</option>
                                <option value="ADJUST">{t('views.modal.creditHistory.filterAdjust', 'Ajustements')}</option>
                            </select>
                        </div>

                        {/* Liste des transactions */}
                        {loading ? (
                            <div className="text-center py-5">
                                <Spiner className="text-warning spinner-border-sm" />
                                <p className="mt-2 text-muted">{t('views.modal.creditHistory.loading', 'Chargement...')}</p>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger">
                                <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        ) : transactions.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fa-solid fa-inbox fa-3x text-muted mb-3"></i>
                                <p className="text-muted">{t('views.modal.creditHistory.noTransactions', 'Aucune transaction trouvée')}</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-dark table-hover">
                                        <thead className="table-secondary sticky-top">
                                            <tr>
                                                <th>{t('views.modal.creditHistory.table.date', 'Date')}</th>
                                                <th>{t('views.modal.creditHistory.table.type', 'Type')}</th>
                                                <th>{t('views.modal.creditHistory.table.amount', 'Montant')}</th>
                                                <th>{t('views.modal.creditHistory.table.balance', 'Solde')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((transaction) => (
                                                <tr key={transaction.id}>
                                                    <td className="text-muted small">
                                                        {formatDate(transaction.created_at)}
                                                    </td>
                                                    <td>
                                                        <span className="me-2">{getTransactionIcon(transaction.transaction_type)}</span>
                                                        <span className="small">{getTransactionLabel(transaction)}</span>
                                                        {transaction.metadata?.process_name && (
                                                            <div className="text-muted small mt-1">
                                                                {transaction.metadata.process_name}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className={getTransactionColor(transaction.transaction_type)}>
                                                        {transaction.transaction_type === 'DEDUCT' ? '-' : '+'}
                                                        {Math.abs(transaction.credits_amount)}
                                                    </td>
                                                    <td className="text-muted small">
                                                        {transaction.credits_after} {t('views.modal.creditHistory.credits', 'crédits')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted small">
                                            {t('views.modal.creditHistory.pageInfo', 'Page {{current}} sur {{total}}', {
                                                current: currentPage,
                                                total: totalPages
                                            })}
                                        </div>
                                        <div>
                                            <button
                                                className="btn btn-sm btn-secondary me-2"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                            >
                                                <i className="fa-solid fa-chevron-left me-1"></i>
                                                {t('words.previous', 'Précédent')}
                                            </button>
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                            >
                                                {t('words.next', 'Suivant')}
                                                <i className="fa-solid fa-chevron-right ms-1"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            {t('words.close', 'Fermer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default CreditHistoryModal;

